import { useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import type { ProjectionYear } from '../engine/projection';
import type { Compensation, ContributionRule } from '../types';
import { fmtCompact, fmtMoney } from '../utils/format';
import { resolveRuleAnnual } from '../engine/contributions';

type SeriesKey = 'netWorth' | 'netWorthNoPurchases' | 'investable' | 'taxable' | 'retirement' | 'hysa' | 'home' | 'mortgage' | 'actualNetWorth';

interface Series {
  key: SeriesKey;
  label: string;
  color: string;
  type: 'line' | 'area';
  strokeWidth?: number;
  dashed?: boolean;
  dotted?: boolean;
}

const ALL_SERIES: Series[] = [
  { key: 'netWorth', label: 'Net Worth (proj.)', color: '#3b54e8', type: 'area', strokeWidth: 2.5 },
  { key: 'netWorthNoPurchases', label: 'No purchases (ghost)', color: '#3b54e8', type: 'line', strokeWidth: 1.5, dashed: true },
  { key: 'investable', label: 'Investable (proj.)', color: '#059669', type: 'line', strokeWidth: 2 },
  { key: 'taxable', label: 'Taxable', color: '#f59e0b', type: 'line', strokeWidth: 1.5, dashed: true },
  { key: 'retirement', label: '401(k)', color: '#8b5cf6', type: 'line', strokeWidth: 1.5, dashed: true },
  { key: 'hysa', label: 'HYSA', color: '#0891b2', type: 'line', strokeWidth: 1.5, dashed: true },
  { key: 'home', label: 'Home − Mortgage', color: '#78716c', type: 'line', strokeWidth: 1.5, dotted: true },
  { key: 'actualNetWorth', label: 'Net Worth (actual)', color: '#0b0f1c', type: 'line', strokeWidth: 2.5 },
];

const DEFAULT_ON: SeriesKey[] = ['netWorth', 'netWorthNoPurchases', 'investable', 'actualNetWorth'];

interface CompareSeries {
  id: string;
  label: string;
  color: string;
  years: ProjectionYear[];
}

export type ChartEventTone = 'neg' | 'pos' | 'accent' | 'warning' | 'neutral' | 'muted';

export interface ChartEvent {
  yearOffset: number;
  calendarYear: number;
  name: string;
  kind: string;
  tone: ChartEventTone;
}

interface Props {
  years: ProjectionYear[];
  baselineYears?: ProjectionYear[];
  /** Additional scenario overlays (rendered as dashed lines in distinct colors). */
  compareSeries?: CompareSeries[];
  targetRetirementAge: number;
  requiredNestEgg: number;
  actuals?: { date: string; netWorth: number; investable: number }[];
  startingCalendarYear: number;
  currentAge: number;
  /** Every enabled event in the active scenario. Rendered as annotated
   *  markers on the net-worth line; color follows the event's tone. */
  chartEvents?: ChartEvent[];
  /** Compensation block. When provided alongside `contributionRules`, the
   *  "Show recurring events" toggle becomes available — surfaces the bonus
   *  month and lump-sum / with-bonus contribution months as faint vertical
   *  ticks across every projection year. */
  compensation?: Compensation;
  contributionRules?: ContributionRule[];
}

const TONE_COLOR: Record<ChartEventTone, string> = {
  neg: '#dc2626',
  pos: '#059669',
  accent: '#4f6bff',
  warning: '#f59e0b',
  neutral: '#525b6f',
  muted: '#94a3b8',
};

export function NetWorthChart({
  years,
  baselineYears,
  compareSeries,
  targetRetirementAge,
  requiredNestEgg,
  actuals,
  startingCalendarYear,
  currentAge,
  chartEvents,
  compensation,
  contributionRules,
}: Props) {
  // Slice 10: optional toggle that overlays one faint vertical tick per year
  // for each *recurring* sub-annual marker — bonus month + every lump-sum /
  // with-bonus contribution month. Off by default (50 years × 3 markers is a
  // lot of tick marks), but powerful for spotting cadence changes.
  const [showRecurring, setShowRecurring] = useState(false);
  const canShowRecurring =
    !!compensation &&
    !!contributionRules &&
    contributionRules.length > 0 &&
    years.length > 0;
  // Use purchasePlanned (raw input) so the ghost line appears even when all
  // active purchases are funded by cash_flow / home_sale / mortgage — which
  // don't touch any bucket and would otherwise leave purchaseTotal at 0.
  const hasPurchases = years.some(y => (y.purchasePlanned ?? 0) > 0);
  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>(() => {
    const m = Object.fromEntries(ALL_SERIES.map(s => [s.key, DEFAULT_ON.includes(s.key)])) as Record<SeriesKey, boolean>;
    return m;
  });

  // Group all events (from the active scenario) by their calendar year. One
  // marker per year; if multiple events share a year we summarize them in
  // a single label ("Truck + 2 more"). Color follows the dominant event tone.
  const eventMarkers = useMemo(() => {
    const byYear = new Map<number, ChartEvent[]>();
    for (const e of chartEvents ?? []) {
      const list = byYear.get(e.calendarYear) ?? [];
      list.push(e);
      byYear.set(e.calendarYear, list);
    }
    const netWorthByYear = new Map<number, number>();
    for (const y of years) netWorthByYear.set(y.calendarYear, Math.round(y.netWorth));
    const markers: {
      year: number;
      label: string;
      detail: string;
      color: string;
      netWorth: number;
    }[] = [];
    for (const [year, evs] of byYear.entries()) {
      const first = evs[0];
      const allSameTone = evs.every(e => e.tone === first.tone);
      const tone: ChartEventTone = allSameTone ? first.tone : 'neutral';
      const label = evs.length === 1 ? first.name : `${first.name} +${evs.length - 1}`;
      const detail = evs.map(e => e.name).join(', ');
      markers.push({
        year,
        label,
        detail,
        color: TONE_COLOR[tone],
        netWorth: netWorthByYear.get(year) ?? 0,
      });
    }
    return markers.sort((a, b) => a.year - b.year);
  }, [chartEvents, years]);
  const eventsByYear = useMemo(() => {
    const m: Record<number, string> = {};
    for (const e of eventMarkers) m[e.year] = e.detail;
    return m;
  }, [eventMarkers]);

  // Synthesize recurring sub-annual tick positions across the projection.
  // One tick per (year, month) for: bonus + every lump_sum / with_bonus rule.
  // Encoded as fractional years on the chart's X axis (Jan = year, Dec ≈
  // year + 0.917). Recharts is OK with fractional x values when type=number.
  const recurringTicks = useMemo<{ x: number; color: string }[]>(() => {
    if (!showRecurring || !canShowRecurring) return [];
    const months: { month: number; color: string }[] = [];
    if (compensation && compensation.bonusAnnual > 0) {
      months.push({ month: compensation.bonusMonth, color: '#059669' });
    }
    for (const rule of contributionRules ?? []) {
      if (!rule.enabled) continue;
      if (compensation && resolveRuleAnnual(rule, compensation) <= 0) continue;
      if (rule.schedule.kind === 'lump_sum') {
        months.push({ month: rule.schedule.month, color: '#3b54e8' });
      } else if (rule.schedule.kind === 'with_bonus' && compensation) {
        months.push({ month: compensation.bonusMonth, color: '#7c3aed' });
      }
    }
    if (months.length === 0) return [];
    const out: { x: number; color: string }[] = [];
    for (const y of years) {
      for (const m of months) {
        out.push({
          x: y.calendarYear + (m.month - 1) / 12,
          color: m.color,
        });
      }
    }
    return out;
  }, [showRecurring, canShowRecurring, compensation, contributionRules, years]);

  // Defensive: chart math should never receive NaN / Infinity even though
  // the engine already clamps to >= 0 in withdrawal logic. Keep this filter
  // so any future engine regression degrades gracefully instead of breaking
  // Recharts' SVG path rendering.
  const safeRound = (v: number | undefined | null) =>
    v == null || !Number.isFinite(v) ? 0 : Math.round(v);

  const data = useMemo(() => {
    const base: Record<
      number,
      Record<string, number | undefined> & {
        year: number;
        age: number;
        overstretched?: 1;
      }
    > = {};
    for (const y of years) {
      base[y.calendarYear] = {
        year: y.calendarYear,
        age: y.age,
        netWorth: safeRound(y.netWorth),
        investable: safeRound(y.investable),
        taxable: safeRound(y.taxable.end),
        retirement: safeRound(y.retirement.end),
        hysa: safeRound(y.hysa.end),
        home: safeRound(y.homeValue - y.mortgageBalance),
        purchase: y.purchaseTotal > 0 ? safeRound(y.purchaseTotal) : undefined,
        overstretched: y.overstretched ? 1 : undefined,
      };
    }
    if (hasPurchases && baselineYears) {
      for (const y of baselineYears) {
        const row = base[y.calendarYear];
        if (row) row.netWorthNoPurchases = safeRound(y.netWorth);
      }
    }
    for (const cs of compareSeries ?? []) {
      for (const y of cs.years) {
        if (!base[y.calendarYear]) {
          base[y.calendarYear] = { year: y.calendarYear, age: y.age };
        }
        base[y.calendarYear][`compare_${cs.id}`] = safeRound(y.netWorth);
      }
    }
    for (const a of actuals ?? []) {
      const dt = new Date(a.date);
      const yr = dt.getFullYear();
      if (!base[yr]) base[yr] = { year: yr, age: yr - startingCalendarYear + currentAge };
      base[yr].actualNetWorth = safeRound(a.netWorth);
    }
    return Object.values(base).sort((a, b) => a.year - b.year);
    // safeRound is referentially stable inside this component scope
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years, baselineYears, compareSeries, actuals, hasPurchases, startingCalendarYear, currentAge]);

  // Slice 6: identify a contiguous tail of overstretched years so we can fade
  // chart confidence at the right side of the projection (the portion where
  // the model's predictive power is dropping). We only fade if the trailing
  // run is non-trivial — single isolated overstretched years in mid-chart
  // shouldn't gray out the entire tail.
  const fadedFromYear = useMemo(() => {
    if (years.length === 0) return null;
    let runLen = 0;
    for (let i = years.length - 1; i >= 0; i--) {
      if (years[i].overstretched) runLen++;
      else break;
    }
    if (runLen < 3) return null;
    return years[years.length - runLen].calendarYear;
  }, [years]);
  // Detect whether any year carries a negative net worth — used to decide
  // whether to render the y=0 reference guide. Hiding it when everything is
  // comfortably positive avoids visual noise.
  const hasBelowZero = useMemo(
    () => years.some(y => y.netWorth < 0),
    [years],
  );

  const retirementYear = startingCalendarYear + (targetRetirementAge - currentAge);

  const [hoverYear, setHoverYear] = useState<number | null>(null);
  const hoverRow = hoverYear != null ? data.find(d => d.year === hoverYear) : null;

  // Impact summary at the retirement horizon
  const retirementIdx = Math.max(0, Math.min(targetRetirementAge - currentAge, years.length - 1));
  const totalPlanned = years.reduce((s, y) => s + (y.purchasePlanned ?? 0), 0);
  const totalPulled = years.reduce((s, y) => s + y.purchaseTotal, 0);
  const totalUnfunded = years.reduce((s, y) => s + (y.purchaseUnfunded ?? 0), 0);
  const totalFromRetirement = years.reduce((s, y) => s + (y.purchaseFromRetirement ?? 0), 0);
  const impact =
    hasPurchases && baselineYears
      ? {
          nestEgg:
            (baselineYears[retirementIdx]?.netWorth ?? 0) -
            (years[retirementIdx]?.netWorth ?? 0),
        }
      : null;

  const showImpact = !!impact && totalPlanned > 0;

  return (
    <div className="space-y-3">
      {/* Always-present banner with fixed footprint so toggling purchases
          doesn't reflow the chart below. Content swaps between idle/impact. */}
      <div
        className={`flex min-h-[76px] items-start gap-3 rounded-xl border px-3 py-2 text-xs transition-colors ${
          showImpact
            ? 'border-neg-500/30 bg-neg-50/60 dark:bg-neg-700/10 dark:border-neg-500/40'
            : 'border-ink-100 bg-ink-50/60 dark:bg-ink-800/40 dark:border-ink-700'
        }`}
      >
        {showImpact ? (
          <>
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neg-500 text-white text-[11px] font-bold">
              $!
            </span>
            <div className="flex-1 space-y-0.5">
              <div className="font-semibold text-ink-900 dark:text-ink-50">
                Planned purchases: {fmtMoney(totalPlanned)}
              </div>
              <div className="text-ink-500 dark:text-ink-300">
                Reduces nest egg at age {targetRetirementAge} by{' '}
                <span className="font-mono font-semibold tabular-nums text-neg-700 dark:text-neg-500">
                  −{fmtMoney(impact!.nestEgg)}
                </span>{' '}
                (incl. lost growth). Compare the dashed ghost line.
              </div>
              {totalFromRetirement > 0 && (
                <div className="text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                  ⚠ {fmtMoney(totalFromRetirement)} spilled over from 401(k) —
                  pre-59½ withdrawals incur tax + 10% penalty IRL unless via 72(t) SEPP.
                </div>
              )}
              {totalUnfunded > 0 && (
                <div className="text-[11px] font-semibold text-neg-700 dark:text-neg-500">
                  ⚠ {fmtMoney(totalUnfunded)} unfunded — no bucket had enough to cover this.
                </div>
              )}
              {Math.abs(totalPulled + totalUnfunded - totalPlanned) > 1 && (
                <div className="text-[11px] text-ink-500">
                  Actual pulled: {fmtMoney(totalPulled)}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ink-200 text-ink-600 text-[11px] font-bold dark:bg-ink-700 dark:text-ink-300">
              ○
            </span>
            <div className="flex-1 self-center text-ink-500 dark:text-ink-300">
              No planned purchases active. Toggle one in the panel to see its impact on the chart.
            </div>
          </>
        )}
      </div>

      {canShowRecurring && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-ink-500 dark:text-ink-300">
          <span>
            Showing year-end net worth across the projection.
          </span>
          <label className="inline-flex cursor-pointer items-center gap-1.5 select-none">
            <input
              type="checkbox"
              className="h-3 w-3 accent-accent-500"
              checked={showRecurring}
              onChange={e => setShowRecurring(e.target.checked)}
            />
            Show recurring events
            {showRecurring && (
              <span className="ml-1 text-[10px] text-ink-400">
                ({recurringTicks.length} ticks)
              </span>
            )}
          </label>
        </div>
      )}

      <div className="h-[440px] w-full">
        <ResponsiveContainer>
          <ComposedChart
            data={data}
            // Tight top/right margins just enough for 45°-tilted purchase
            // labels; everything else gives the plot maximum real estate.
            margin={{ top: 68, right: 52, left: 0, bottom: 0 }}
            onMouseMove={state => {
              if (state?.activeLabel != null) setHoverYear(Number(state.activeLabel));
            }}
            onMouseLeave={() => setHoverYear(null)}
          >
            <defs>
              <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4f6bff" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#4f6bff" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#eef0f4" strokeDasharray="3 6" vertical={false} />
            <XAxis
              dataKey="year"
              type="number"
              domain={['dataMin', 'dataMax']}
              tick={{ fill: '#828a9c', fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: '#dde1e9' }}
            />
            <YAxis
              tickFormatter={v => fmtCompact(v)}
              tick={{ fill: '#828a9c', fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: '#dde1e9' }}
              width={42}
            />
            <Tooltip
              cursor={{ stroke: '#525b6f', strokeDasharray: '2 4' }}
              content={
                <RichTooltip
                  visible={visible}
                  currentAge={currentAge}
                  startingCalendarYear={startingCalendarYear}
                  eventsByYear={eventsByYear}
                />
              }
            />
            <ReferenceLine
              x={retirementYear}
              stroke="#525b6f"
              strokeDasharray="3 3"
              label={{ value: `Retire @ ${targetRetirementAge}`, position: 'insideTop', fill: '#525b6f', fontSize: 11 }}
            />
            {hasBelowZero && (
              // Subtle horizontal guide: shown only when at least one year
              // dips below zero, so the eye can read "this is where the
              // projection runs out" rather than mistaking the dip for a
              // graphics glitch. Color is muted slate, never red.
              <ReferenceLine
                y={0}
                stroke="#94a3b8"
                strokeDasharray="2 4"
                strokeOpacity={0.6}
                label={{
                  value: 'Net worth = 0',
                  position: 'insideBottomLeft',
                  fill: '#94a3b8',
                  fontSize: 10,
                }}
              />
            )}
            {fadedFromYear != null && (
              // Visual cue that the tail of the projection is in
              // "low-confidence territory" — repeated overstretched years
              // mean the engine is past where its assumptions are reliable.
              // We render a faint fill band rather than red overflow.
              <ReferenceLine
                x={fadedFromYear}
                stroke="#cbd5e1"
                strokeDasharray="1 3"
                strokeOpacity={0.7}
                label={{
                  value: 'Confidence drops →',
                  position: 'insideTopRight',
                  fill: '#94a3b8',
                  fontSize: 10,
                }}
              />
            )}
            {requiredNestEgg > 0 && (
              <ReferenceLine
                y={requiredNestEgg}
                stroke="#ef4444"
                strokeDasharray="3 3"
                strokeOpacity={0.5}
                label={{ value: `Target ${fmtCompact(requiredNestEgg)}`, position: 'insideBottomRight', fill: '#ef4444', fontSize: 11 }}
              />
            )}

            {/* Slice 10: faint sub-annual ticks for recurring events.
                Bonus + lump-sum / with-bonus contributions, one per year.
                Rendered behind the named-event markers. */}
            {recurringTicks.map((t, i) => (
              <ReferenceLine
                key={`rec-${i}`}
                x={t.x}
                stroke={t.color}
                strokeWidth={0.75}
                strokeOpacity={0.18}
                ifOverflow="extendDomain"
              />
            ))}

            {eventMarkers.map(p => {
              const labelText =
                p.label.length > 20 ? p.label.slice(0, 19) + '…' : p.label;
              return (
                <ReferenceLine
                  key={`eline-${p.year}`}
                  x={p.year}
                  stroke={p.color}
                  strokeWidth={1}
                  strokeDasharray="2 3"
                  strokeOpacity={0.55}
                  label={<EventLineLabel text={labelText} color={p.color} />}
                />
              );
            })}
            {eventMarkers.map(p => (
              <ReferenceDot
                key={`edot-${p.year}`}
                x={p.year}
                y={p.netWorth}
                r={5}
                fill={p.color}
                stroke="#fff"
                strokeWidth={2}
                ifOverflow="extendDomain"
              />
            ))}

            {ALL_SERIES.filter(s => visible[s.key]).map(s =>
              s.type === 'area' ? (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={s.strokeWidth}
                  fill="url(#netWorthGradient)"
                  connectNulls
                  isAnimationActive={false}
                  activeDot={{ r: 4, strokeWidth: 2, fill: '#fff', stroke: s.color }}
                />
              ) : (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={s.strokeWidth}
                  strokeDasharray={s.dashed ? '5 4' : s.dotted ? '1 4' : undefined}
                  dot={s.key === 'actualNetWorth' ? { r: 3, fill: s.color } : false}
                  activeDot={{ r: 4, strokeWidth: 2, fill: '#fff', stroke: s.color }}
                  connectNulls
                  isAnimationActive={false}
                />
              ),
            )}

            {/* Compare-scenario overlays: one dashed line per extra scenario */}
            {(compareSeries ?? []).map(cs => (
              <Line
                key={`cmp-${cs.id}`}
                type="monotone"
                dataKey={`compare_${cs.id}`}
                name={cs.label}
                stroke={cs.color}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, fill: '#fff', stroke: cs.color }}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {compareSeries && compareSeries.length > 0 && (
        <div className="flex flex-wrap gap-2 rounded-lg border border-ink-100 bg-ink-50/60 px-3 py-2 text-[11px] dark:border-ink-700 dark:bg-ink-800/40">
          <span className="text-ink-500 dark:text-ink-300">Comparing:</span>
          <span className="inline-flex items-center gap-1.5 font-semibold">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: '#3b54e8' }} />
            Active
          </span>
          {compareSeries.map(cs => (
            <span key={cs.id} className="inline-flex items-center gap-1.5 font-semibold">
              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: cs.color }} />
              {cs.label}
            </span>
          ))}
        </div>
      )}

      <Legend visible={visible} onToggle={k => setVisible(v => ({ ...v, [k]: !v[k] }))} />

      <HoverReadout row={hoverRow} retirementYear={retirementYear} />
    </div>
  );
}

/**
 * Tilted label that rides the dashed reference line of an event marker.
 * Recharts passes a `viewBox` prop for a vertical ReferenceLine containing
 * {x, y, width, height}; we anchor just above the plot area and rotate −45°
 * so adjacent-year labels fan out diagonally without overlapping.
 */
function EventLineLabel(props: {
  text: string;
  color: string;
  viewBox?: { x: number; y: number; width: number; height: number };
}) {
  const { text, color, viewBox } = props;
  if (!viewBox) return null;
  const { x, y } = viewBox;
  const cy = y - 3;
  return (
    <text
      x={x}
      y={cy}
      fill={color}
      fontSize={10}
      fontWeight={500}
      textAnchor="start"
      dx={2}
      opacity={0.9}
      style={{ pointerEvents: 'none' }}
      transform={`rotate(-45, ${x}, ${cy})`}
    >
      {text}
    </text>
  );
}

function Legend({
  visible,
  onToggle,
}: {
  visible: Record<SeriesKey, boolean>;
  onToggle: (k: SeriesKey) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {ALL_SERIES.map(s => {
        const on = visible[s.key];
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onToggle(s.key)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
              on
                ? 'border-ink-200 bg-white text-ink-900 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-50'
                : 'border-ink-100 bg-ink-50 text-ink-400 dark:border-ink-800 dark:bg-ink-900 dark:text-ink-300'
            }`}
            aria-pressed={on}
          >
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: on ? s.color : '#cbd5e1' }}
            />
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

function HoverReadout({
  row,
  retirementYear,
}: {
  row: ReturnType<typeof useMemo> extends never ? null : Record<string, number | undefined> & { year: number; age: number } | null | undefined;
  retirementYear: number;
}) {
  if (!row) {
    return (
      <div className="h-14 text-[11px] text-ink-400 dark:text-ink-500">
        Drag across the chart or tap a point for details.
      </div>
    );
  }
  const status = row.year < retirementYear ? 'Working' : 'Retired';
  return (
    <div className="grid grid-cols-3 gap-2 text-[11px]">
      <div>
        <div className="label">Year</div>
        <div className="font-mono text-sm font-semibold tabular-nums">
          {row.year} · age {row.age} · {status}
        </div>
      </div>
      <div>
        <div className="label">Net Worth</div>
        <div className="font-mono text-sm font-semibold tabular-nums">{fmtMoney(row.netWorth ?? 0)}</div>
      </div>
      <div>
        <div className="label">Investable</div>
        <div className="font-mono text-sm font-semibold tabular-nums">{fmtMoney(row.investable ?? 0)}</div>
      </div>
    </div>
  );
}

function RichTooltip({
  active,
  payload,
  label,
  visible,
  startingCalendarYear,
  currentAge,
  eventsByYear,
}: TooltipProps<number, string> & {
  visible: Record<SeriesKey, boolean>;
  startingCalendarYear: number;
  currentAge: number;
  eventsByYear: Record<number, string>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const year = Number(label);
  const age = year - startingCalendarYear + currentAge;
  const rows = ALL_SERIES.filter(s => visible[s.key])
    .map(s => {
      const p = payload.find(pl => pl.dataKey === s.key);
      const v = p?.value;
      return { s, value: typeof v === 'number' ? v : null };
    })
    .filter(r => r.value != null);
  const eventDetail = eventsByYear[year];
  // Show "cost" = baseline − actual, when both are available
  const nw = rows.find(r => r.s.key === 'netWorth')?.value;
  const baseline = rows.find(r => r.s.key === 'netWorthNoPurchases')?.value;
  const cost = nw != null && baseline != null ? baseline - nw : null;

  return (
    <div className="rounded-xl border border-ink-200 bg-white/98 p-3 shadow-card backdrop-blur dark:bg-ink-800/98 dark:border-ink-700">
      <div className="mb-2 border-b border-ink-100 pb-1.5 text-xs dark:border-ink-700">
        <span className="font-mono font-semibold tabular-nums">{year}</span>
        <span className="ml-2 text-ink-500 dark:text-ink-300">age {age}</span>
      </div>
      <table className="min-w-[180px] text-xs">
        <tbody>
          {rows.map(r => (
            <tr key={r.s.key}>
              <td className="pr-3 py-0.5">
                <span className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: r.s.color }} />
                <span className="text-ink-700 dark:text-ink-100">{r.s.label}</span>
              </td>
              <td className="py-0.5 text-right font-mono font-semibold tabular-nums">{fmtMoney(r.value!)}</td>
            </tr>
          ))}
          {eventDetail && (
            <tr className="border-t border-ink-100 dark:border-ink-700">
              <td colSpan={2} className="pt-1.5 text-[11px] text-ink-700 dark:text-ink-100">
                <span className="font-semibold">Events:</span> {eventDetail}
              </td>
            </tr>
          )}
          {cost != null && cost > 0 && (
            <tr className={eventDetail ? '' : 'border-t border-ink-100 dark:border-ink-700'}>
              <td className="pr-3 pt-1 text-ink-500 dark:text-ink-300 italic">Cumulative cost</td>
              <td className="pt-1 text-right font-mono font-semibold tabular-nums text-neg-700 dark:text-neg-500">
                −{fmtMoney(cost)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
